import { useMemo } from 'react';
import { DataType, FormatType, useNumberFormatter } from './useNumberFormatter';

const COMPACT_THRESHOLD = 1_000;
const PERCENT_SCALE = 100;

const withCompactUnits =
  (
    base: (v: number) => string,
    compact: (v: number) => string,
    scale: number,
  ) =>
  (v: number): string =>
    Math.abs(v * scale) < COMPACT_THRESHOLD ? base(v) : compact(v);

export const useCompactNumberFormatter = (
  currency: string,
): Record<DataType, Record<FormatType, (v: number) => string>> => {
  const baseMap = useNumberFormatter(currency);

  return useMemo(() => {
    const compactOptions = {
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: 1,
    } as const;

    const compactNumberFormatter = new Intl.NumberFormat(
      'en-US',
      compactOptions,
    ).format;
    const compactCurrencyFormatter = new Intl.NumberFormat('en-US', {
      ...compactOptions,
      style: 'currency',
      currency,
    }).format;
    const compactPercentFormatter = new Intl.NumberFormat('en-US', {
      ...compactOptions,
      style: 'percent',
    }).format;

    const buildFormatters = (
      formatters: Record<FormatType, (v: number) => string>,
      compact: (v: number) => string,
      scale: number,
    ): Record<FormatType, (v: number) => string> => ({
      [FormatType.INTEGER]: withCompactUnits(
        formatters[FormatType.INTEGER],
        compact,
        scale,
      ),
      [FormatType.FLOAT]: withCompactUnits(
        formatters[FormatType.FLOAT],
        compact,
        scale,
      ),
      [FormatType.FLOAT_4]: withCompactUnits(
        formatters[FormatType.FLOAT_4],
        compact,
        scale,
      ),
    });

    return {
      [DataType.CURRENCY]: buildFormatters(
        baseMap[DataType.CURRENCY],
        compactCurrencyFormatter,
        1,
      ),
      [DataType.PERCENTAGE]: buildFormatters(
        baseMap[DataType.PERCENTAGE],
        compactPercentFormatter,
        PERCENT_SCALE,
      ),
      [DataType.NUMBER]: buildFormatters(
        baseMap[DataType.NUMBER],
        compactNumberFormatter,
        1,
      ),
    };
  }, [baseMap, currency]);
};
